import { useState, useEffect } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { API } from "@/App";
import { toast } from "sonner";
import { ArrowLeft, Lock, CheckCircle, XCircle } from "lucide-react";

export const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");
  
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!token) {
      setError("Invalid reset link. Please request a new password reset.");
    }
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    
    if (password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await fetch(`${API}/auth/reset-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, new_password: password })
      });
      
      if (response.ok) {
        setSuccess(true);
        toast.success("Password reset successfully!");
      } else {
        const errorData = await response.json();
        setError(errorData.detail || "Failed to reset password");
        toast.error(errorData.detail || "Failed to reset password");
      }
    } catch (_err) {
      console.error("Reset password error:", _err);
      setError("An error occurred. Please try again.");
      toast.error("An error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-neutral-50 flex">
      {/* Left Panel */}
      <div className="hidden lg:flex lg:w-1/2 bg-[#333333] p-12 flex-col justify-between">
        <div>
          <Link to="/login" className="inline-flex items-center space-x-2 text-white/80 hover:text-white transition-colors">
            <ArrowLeft className="w-4 h-4" />
            <span>Back to login</span>
          </Link>
        </div>
        
        <div className="space-y-6">
          <h1 className="text-4xl font-semibold text-white leading-tight">
            Create New
            <span className="block text-[#39FF14]">Password</span>
          </h1>
          <p className="text-neutral-300 text-lg max-w-md">
            Choose a strong password to secure your account.
          </p>
        </div>
        
        <div className="flex items-center space-x-3">
          <img src="/mybiz-logo.png" alt="My Biz Consulting" className="h-10 w-auto" />
          <div>
            <p className="text-sm font-semibold text-white leading-tight">My Biz Consulting</p>
            <p className="text-[10px] text-[#39FF14] font-medium uppercase tracking-widest">Business Solutions Center</p>
          </div>
        </div>
      </div>

      {/* Right Panel */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md space-y-8">
          <div className="lg:hidden mb-8">
            <Link to="/login" className="inline-flex items-center space-x-2 text-neutral-600 hover:text-neutral-900 transition-colors">
              <ArrowLeft className="w-4 h-4" />
              <span>Back to login</span>
            </Link>
          </div>
          
          {error && !success ? (
            <div className="text-center space-y-6" data-testid="reset-error">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto">
                <XCircle className="w-8 h-8 text-red-600" />
              </div>
              <div>
                <h2 className="text-2xl font-semibold text-neutral-900 mb-2">Reset Failed</h2>
                <p className="text-neutral-600">{error}</p>
              </div>
              <Link to="/forgot-password">
                <Button className="w-full h-12 bg-[#333333] hover:bg-[#444444] text-white font-medium mt-4">
                  Request New Reset Link
                </Button>
              </Link>
            </div>
          ) : success ? (
            <div className="text-center space-y-6" data-testid="reset-success">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <div>
                <h2 className="text-2xl font-semibold text-neutral-900 mb-2">Password Reset!</h2>
                <p className="text-neutral-600">
                  Your password has been successfully reset. You can now sign in with your new password.
                </p>
              </div>
              <Link to="/login">
                <Button className="w-full h-12 bg-[#333333] hover:bg-[#444444] text-white font-medium mt-4">
                  Sign In
                </Button>
              </Link>
            </div>
          ) : (
            <>
              <div className="text-center lg:text-left">
                <h2 className="text-3xl font-semibold text-neutral-900 mb-2">Reset Password</h2>
                <p className="text-neutral-600">Enter your new password below</p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="password" className="text-neutral-700">New Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-400" />
                    <Input
                      id="password"
                      type="password"
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="pl-10 h-12 bg-white border-neutral-200 focus:border-[#39FF14] focus:ring-[#39FF14]"
                      required
                      minLength={8}
                      data-testid="reset-password-input"
                    />
                  </div>
                  <p className="text-xs text-neutral-500">Minimum 8 characters</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirmPassword" className="text-neutral-700">Confirm Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-400" />
                    <Input
                      id="confirmPassword"
                      type="password"
                      placeholder="••••••••"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="pl-10 h-12 bg-white border-neutral-200 focus:border-[#39FF14] focus:ring-[#39FF14]"
                      required
                      data-testid="reset-confirm-password-input"
                    />
                  </div>
                </div>

                <Button 
                  type="submit" 
                  className="w-full h-12 bg-[#333333] hover:bg-[#444444] text-white font-medium touch-manipulation"
                  disabled={loading}
                  data-testid="reset-password-submit-btn"
                >
                  {loading ? "Resetting..." : "Reset Password"}
                </Button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
